/**
 * Desk — shared tab navigation between the module's screens
 * (Today / Inbox / Settings). Rendered at the top of every Desk container.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DESK_MAILBOX_CHANGED_EVENT,
  fetchDeskCardCounts,
  readSelectedDeskMailbox,
} from "services/desk.service";
import { DeskCardCounts } from "types/Desk";

/** Same cadence as the card lists — the badges must not lag behind them. */
const REFRESH_INTERVAL_MS = 30 * 1000;

const TABS: Array<{ path: string; labelKey: string; countKey?: keyof DeskCardCounts }> = [
  { path: "today", labelKey: "desk_today" },
  { path: "inbox", labelKey: "desk_inbox", countKey: "inbox" },
  { path: "shared", labelKey: "desk_shared", countKey: "shared" },
  { path: "drafts", labelKey: "desk_drafts", countKey: "drafts" },
  { path: "archive", labelKey: "desk_archive", countKey: "archive" },
  { path: "trash", labelKey: "desk_trash", countKey: "trash" },
  { path: "settings", labelKey: "desk_settings" },
];

/**
 * DeskNav is mounted inside each tab route, so it remounts on every switch.
 * Keep the last successful counts in module scope so badges do not flash off
 * while the next fetch is in flight.
 */
let countsCache: { key: string; data: DeskCardCounts } | null = null;

function countsCacheKey(companyId: string): string {
  return `${companyId}:${readSelectedDeskMailbox(companyId) || ""}`;
}

function readCachedCounts(companyId: string | undefined): DeskCardCounts | null {
  if (!companyId || !countsCache) return null;
  return countsCache.key === countsCacheKey(companyId) ? countsCache.data : null;
}

/**
 * The tab bar scrolls sideways on a phone and has no scrollbar, so the edge
 * that still has tabs behind it fades out — the only hint that it can be swiped.
 */
function scrollFadeCls({ start, end }: { start: boolean; end: boolean }): string {
  if (start && end) {
    return "[mask-image:linear-gradient(to_right,transparent,#000_1.5rem,#000_calc(100%_-_1.5rem),transparent)]";
  }
  if (end) return "[mask-image:linear-gradient(to_right,#000_calc(100%_-_1.5rem),transparent)]";
  if (start) return "[mask-image:linear-gradient(to_right,transparent,#000_1.5rem)]";
  return "";
}

export default function DeskNav() {
  const { companyId } = useParams<{ companyId: string }>();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [counts, setCounts] = useState<DeskCardCounts | null>(() =>
    readCachedCounts(companyId)
  );
  const barRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ start: false, end: false });

  const measureFade = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    const start = bar.scrollLeft > 1;
    const end = bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 1;
    // Same edges as before: keep the old object so a scroll tick does not re-render the bar.
    setFade((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  // Bring the open tab into view: Nustatymai sits past the right edge of a phone.
  useEffect(() => {
    const bar = barRef.current;
    const active = bar?.querySelector<HTMLElement>('[aria-current="page"]');
    if (bar && active) {
      bar.scrollLeft = active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2;
    }
    measureFade();
    window.addEventListener("resize", measureFade);
    return () => window.removeEventListener("resize", measureFade);
  }, [pathname, measureFade]);

  // Badges are chrome: a failed poll keeps the last known values, it never
  // clears them (that caused tab badges to blink on every route remount).
  // They follow the list's mailbox switcher, otherwise a user with two
  // mailboxes reads "11" on the tab above a list of 3.
  useEffect(() => {
    if (!companyId) return undefined;
    let alive = true;
    const applyCached = () => {
      const cached = readCachedCounts(companyId);
      setCounts(cached || null);
    };
    const load = () => {
      const key = countsCacheKey(companyId);
      fetchDeskCardCounts(companyId, readSelectedDeskMailbox(companyId))
        .then((data) => {
          if (!alive) return;
          countsCache = { key, data };
          setCounts(data);
        })
        .catch(() => {
          // Keep last known counts — never blank the badges on a failed poll.
        });
    };
    applyCached();
    load();
    const timer = setInterval(load, REFRESH_INTERVAL_MS);
    const onMailboxChanged = () => {
      applyCached();
      load();
    };
    window.addEventListener(DESK_MAILBOX_CHANGED_EVENT, onMailboxChanged);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener(DESK_MAILBOX_CHANGED_EVENT, onMailboxChanged);
    };
    // Refetch on tab change too — acting on cards moves them between tabs.
  }, [companyId, pathname]);

  return (
    <div
      ref={barRef}
      onScroll={measureFade}
      className={
        "no-scrollbar relative mb-4 flex gap-1 overflow-x-auto whitespace-nowrap border-b border-zinc-200 text-sm dark:border-zinc-700 " +
        scrollFadeCls(fade)
      }
    >
      {TABS.map((tab) => {
        const isActive = pathname.includes(`/desk/${tab.path}`);
        const count = tab.countKey && counts ? counts[tab.countKey] : 0;
        return (
          <Link
            key={tab.path}
            to={`/app/${companyId}/desk/${tab.path}`}
            aria-current={isActive ? "page" : undefined}
            className={
              "flex shrink-0 items-center gap-1.5 px-4 py-2 max-md:py-3 " +
              (isActive
                ? "border-b-2 border-zinc-700 font-medium text-zinc-900 dark:border-zinc-200 dark:text-zinc-100"
                : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300")
            }
          >
            {t(tab.labelKey)}
            {count > 0 && (
              <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[11px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                {count > 999 ? "999+" : count}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
